import React from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';
import lodashCompact from 'lodash/fp/compact';

import {
  SR_UTILITY_ERROR_ADDRESS_ALREADY_ACTIVATED_SAME_PHONE,
  SR_UTILITY_ERROR_ADDRESS_ALREADY_ACTIVATED_DIFFERENT_PHONE,
} from 'common/errorCodes';

import { hasRoleRenter } from 'common/utility/userRoles';

import { createInitialValues, createFormValidationFn } from 'client/helpers/formikHelpers';

import SrFormikField, { SR_FIELD_THEME_ADOBE } from 'client/components/SrFormikField';
import ButtonDeprecated, { BUTTON_THEME_GREEN_ADOBE } from 'client/components/ButtonDeprecated';
import SrBox from 'client/components/SrBox';
import SrLink from 'client/components/SrLink/SrLink';
import SrErrorMessage, {
  SR_ERROR_MESSAGE_THEME_RED,
} from 'client/components/SrErrorMessage/SrErrorMessage';

import './SrUtilitySetupPage.less';

export const getUtilitySetupSignupFormFields = ({
  isRenterContactInformationForm = false,
} = {}) => {
  return lodashCompact([
    {
      id: 'firstName',
      title: isRenterContactInformationForm ? 'Renter first name' : 'First Name',
      type: 'short_text',
      properties: {
        placeholder: isRenterContactInformationForm
          ? 'type first name here...'
          : 'type your first name...',
      },
      validations: ['required'],
    },
    {
      id: 'lastName',
      title: isRenterContactInformationForm ? 'Renter last name' : 'Last Name',
      type: 'short_text',
      properties: {
        placeholder: isRenterContactInformationForm
          ? 'type last name here...'
          : 'type your last name...',
      },
      validations: ['required'],
    },
    {
      id: 'leaseStartDate',
      title: 'Lease Start Date',
      type: 'date_picker',
      properties: {
        dateWithoutTime: true,
        placeholder: isRenterContactInformationForm
          ? 'Select lease start date...'
          : 'select your date...',
      },
      validations: ['required'],
    },
    {
      id: 'phone',
      title: 'Phone',
      type: 'phone',
      properties: {
        placeholder: isRenterContactInformationForm ? 'type phone here...' : 'type your phone...',
      },
      validations: ['required', 'phone'],
    },
    {
      id: 'email',
      title: 'Email',
      type: 'email',
      properties: {
        placeholder: isRenterContactInformationForm ? 'type email here...' : 'type your email...',
      },
      validations: ['required', 'email'],
    },
    !isRenterContactInformationForm && {
      id: 'password',
      title: 'Password',
      type: 'password',
      properties: {
        placeholder: 'type your password...',
      },
      validations: ['required'],
    },
    !isRenterContactInformationForm && {
      id: 'confirmPassword',
      title: 'Confirm Password',
      type: 'password',
      properties: {
        placeholder: 'confirm your password again...',
      },
      validations: ['required', 'confirmPassword:password'],
    },
  ]);
};

export const SrUtilitySetupSignupErrorMessage = ({ error, loginUrl }) => {
  const isSrUtilityErrorAddressAlreadyActivatedDifferentPhone =
    error.name === SR_UTILITY_ERROR_ADDRESS_ALREADY_ACTIVATED_DIFFERENT_PHONE &&
    error.details?.phoneLastDigits;
  const isSrUtilityErrorAddressAlreadyActivatedSamePhone =
    error.name === SR_UTILITY_ERROR_ADDRESS_ALREADY_ACTIVATED_SAME_PHONE;
  const errorMessage =
    error &&
    ((error.constraint === 'utilityusers_phone_unique' &&
      'There is already an account with that phone number.') ||
      (error.constraint === 'utilityusers_email_unique' &&
        'There is already an account with that email.') ||
      error.message);

  return (
    <SrBox marginTop="10px" borderRadius="3px">
      <SrErrorMessage theme={SR_ERROR_MESSAGE_THEME_RED}>
        {errorMessage}
        {isSrUtilityErrorAddressAlreadyActivatedDifferentPhone && (
          <span>
            {' '}
            Please{' '}
            <SrLink style={{ textDecoration: 'underline' }} to={loginUrl}>
              login
            </SrLink>{' '}
            using the phone number ending in {error.details.phoneLastDigits} to continue.
          </span>
        )}
        {isSrUtilityErrorAddressAlreadyActivatedSamePhone && (
          <span>
            {' '}
            Please{' '}
            <SrLink style={{ textDecoration: 'underline' }} to={loginUrl}>
              login
            </SrLink>{' '}
            to continue.
          </span>
        )}
      </SrErrorMessage>
    </SrBox>
  );
};

const SrUtilitySetupSignup = ({ utilityUser, utilityPartner, onSubmit, apiState }) => {
  const canContinueFlow = !utilityUser?.id || hasRoleRenter(utilityUser?.roles || []);

  return (
    <Formik
      onSubmit={onSubmit}
      initialValues={createInitialValues({
        formFields: getUtilitySetupSignupFormFields(),
        defaultValues: {
          firstName: '',
          lastName: '',
          phone: '',
          email: '',
          password: '',
          confirmPassword: '',
          leaseStartDate: null,
        },
      })}
      validate={(formValues) => {
        const formFields = getUtilitySetupSignupFormFields();
        return createFormValidationFn(formFields)(formValues);
      }}
      render={(formikBag) => {
        const formFields = getUtilitySetupSignupFormFields();

        return (
          <Form styleName="view">
            <div styleName="titleBox">
              <span styleName="greenText">
                Address found! <br />
              </span>
              <span>Start setting up utilities:</span>
            </div>
            <div styleName="contentBox">
              <div styleName="cardBox">
                {formFields.map((field) => (
                  <SrFormikField
                    key={field.id}
                    formField={field}
                    formikBag={formikBag}
                    theme={SR_FIELD_THEME_ADOBE}
                    className="utilityProviderQuestion"
                  />
                ))}

                <SrBox margin="21px 0 0">
                  <ButtonDeprecated
                    type="submit"
                    theme={BUTTON_THEME_GREEN_ADOBE}
                    disabled={apiState?.inProgress || !canContinueFlow}
                    onClick={formikBag.handleSubmit}
                  >
                    Continue
                  </ButtonDeprecated>
                  {apiState?.error && (
                    <SrUtilitySetupSignupErrorMessage
                      error={apiState.error.error || apiState.error}
                      loginUrl={`/utility-setup/${utilityPartner.slug}/login`}
                    />
                  )}
                  {!canContinueFlow && (
                    <SrUtilitySetupSignupErrorMessage
                      error={{
                        message: 'Sorry, only renters can continue.',
                      }}
                    />
                  )}
                </SrBox>
                <SrBox textAlign="center" margin="20px 0 0">
                  <SrLink styleName="linkText" to={`/utility-setup/${utilityPartner.slug}/login`}>
                    Already signed up? Login here →
                  </SrLink>
                </SrBox>
              </div>
            </div>
          </Form>
        );
      }}
    />
  );
};

SrUtilitySetupSignup.propTypes = {
  utilityUser: PropTypes.object,
  utilityPartner: PropTypes.object,
  onSubmit: PropTypes.func,
  apiState: PropTypes.object,
};

SrUtilitySetupSignup.displayName = 'SrUtilitySetupSignup';

export default SrUtilitySetupSignup;
